import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Send, RefreshCw, Search, MessageSquare, Users, Megaphone } from "lucide-react";

function DialogIcon({ d }) {
  if (d.is_channel) return <Megaphone className="w-4 h-4" style={{ color: "#7C3AED" }} />;
  if (d.is_group) return <Users className="w-4 h-4" style={{ color: "#0891B2" }} />;
  return <MessageSquare className="w-4 h-4" style={{ color: "#1E40AF" }} />;
}

export default function TelegramChat() {
  const [dialogs, setDialogs] = useState([]);
  const [loadingDialogs, setLoadingDialogs] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState("");
  const scrollRef = useRef(null);

  const loadDialogs = useCallback(async () => {
    setLoadingDialogs(true);
    try {
      const r = await api.get("/integrations/telegram/dialogs", { params: { limit: 50 } });
      setDialogs(r.data.dialogs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingDialogs(false);
    }
  }, []);

  const loadMessages = useCallback(async (d, silent = false) => {
    if (!d) return;
    if (!silent) setLoadingMsgs(true);
    try {
      const r = await api.get("/integrations/telegram/messages", {
        params: { peer_type: d.peer_type, peer_id: d.peer_id, access_hash: d.access_hash, limit: 50 },
      });
      setMessages(r.data.messages || []);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally {
      if (!silent) setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => { loadDialogs(); }, [loadDialogs]);

  // Auto-refresh open conversation every 6s.
  useEffect(() => {
    if (!selected) return;
    const id = setInterval(() => loadMessages(selected, true), 6000);
    return () => clearInterval(id);
  }, [selected, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const selectDialog = (d) => {
    setSelected(d);
    setMessages([]);
    loadMessages(d);
  };

  const sendMsg = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      const r = await api.post("/integrations/telegram/send", {
        peer_type: selected.peer_type,
        peer_id: selected.peer_id,
        access_hash: selected.access_hash,
        text: text.trim(),
      });
      setMessages((m) => [...m, r.data.message]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const filtered = dialogs.filter((d) => d.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="card-shell overflow-hidden" data-testid="telegram-chat" style={{ height: 560 }}>
      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] h-full">
        {/* Dialogs list */}
        <div className="border-r flex flex-col min-h-0" style={{ borderColor: "var(--border)" }}>
          <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca chat…"
                className="form-input pl-8 py-1.5 text-sm w-full"
                data-testid="tg-chat-search"
              />
            </div>
            <button onClick={loadDialogs} className="p-2 rounded-lg hover:bg-black/5" title="Aggiorna" data-testid="tg-chat-refresh-dialogs">
              <RefreshCw className={`w-4 h-4 ${loadingDialogs ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="tg-chat-dialogs">
            {loadingDialogs ? (
              <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>Nessuna chat</div>
            ) : filtered.map((d) => (
              <button
                key={`${d.peer_type}-${d.peer_id}`}
                onClick={() => selectDialog(d)}
                className="w-full text-left px-3 py-2.5 border-b flex items-center gap-2.5 hover:bg-black/5 transition-colors"
                style={{
                  borderColor: "var(--border)",
                  background: selected && selected.peer_id === d.peer_id && selected.peer_type === d.peer_type ? "rgba(30,64,175,0.08)" : "transparent",
                }}
                data-testid={`tg-chat-dialog-${d.peer_id}`}
              >
                <div className="p-1.5 rounded-lg flex-shrink-0" style={{ background: "var(--bg)" }}><DialogIcon d={d} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{d.name || "Senza nome"}</div>
                  <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{d.last_message || "—"}</div>
                </div>
                {d.unread_count > 0 && (
                  <span className="text-[10px] font-bold text-white rounded-full px-1.5 py-0.5" style={{ background: "#1E40AF" }}>
                    {d.unread_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex flex-col min-h-0">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>
              Seleziona una chat per leggere e rispondere
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
                <DialogIcon d={selected} />
                <div className="font-bold truncate" style={{ fontFamily: "Manrope" }}>{selected.name || "Chat"}</div>
                <button onClick={() => loadMessages(selected)} className="ml-auto p-1.5 rounded-lg hover:bg-black/5" title="Aggiorna" data-testid="tg-chat-refresh-messages">
                  <RefreshCw className={`w-4 h-4 ${loadingMsgs ? "animate-spin" : ""}`} />
                </button>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" style={{ background: "var(--bg)" }} data-testid="tg-chat-messages">
                {loadingMsgs ? (
                  <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                ) : messages.length === 0 ? (
                  <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>Nessun messaggio</div>
                ) : messages.map((m) => (
                  <div key={m.id} className={`flex ${m.out ? "justify-end" : "justify-start"}`}>
                    <div
                      className="max-w-[75%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words"
                      style={{
                        background: m.out ? "#1E40AF" : "var(--surface)",
                        color: m.out ? "#fff" : "var(--text)",
                        border: m.out ? "none" : "1px solid var(--border)",
                        borderBottomRightRadius: m.out ? 4 : 16,
                        borderBottomLeftRadius: m.out ? 16 : 4,
                      }}
                    >
                      {m.text || <i style={{ opacity: 0.6 }}>[allegato]</i>}
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendMsg} className="p-3 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Scrivi un messaggio…"
                  className="form-input flex-1"
                  data-testid="tg-chat-input"
                />
                <button type="submit" disabled={sending || !text.trim()} className="btn-primary px-4" data-testid="tg-chat-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
